// 구매 관리 타입 정의

// 공급업체 상태
export type SupplierStatus = 'active' | 'inactive' | 'blocked';

// 발주서 상태
export type PurchaseOrderStatus =
  | 'draft' // 임시저장
  | 'submitted' // 제출됨
  | 'to_receive' // 입고대기
  | 'to_bill' // 청구대기
  | 'completed' // 완료
  | 'cancelled'; // 취소

// 입고 상태
export type PurchaseReceiptStatus =
  | 'draft' // 임시저장
  | 'submitted' // 제출됨
  | 'completed' // 완료
  | 'cancelled'; // 취소

// 구매 청구서 상태
export type PurchaseInvoiceStatus =
  | 'draft' // 임시저장
  | 'submitted' // 제출됨
  | 'unpaid' // 미지급
  | 'partial' // 부분지급
  | 'paid' // 완결
  | 'overdue' // 연체
  | 'cancelled'; // 취소

// 자재 요청 상태
export type MaterialRequestStatus =
  | 'draft' // 임시저장
  | 'submitted' // 제출됨
  | 'pending' // 검토중
  | 'partially_ordered' // 부분발주
  | 'ordered' // 발주완료
  | 'received' // 입고완료
  | 'cancelled'; // 취소

// 자재 요청 유형
export type MaterialRequestType =
  | 'purchase' // 구매
  | 'material_transfer' // 자재이동
  | 'material_issue' // 자재출고
  | 'manufacture'; // 제조

// 공급업체 그룹
export interface SupplierGroup {
  id: string;
  name: string;
  description?: string;
  supplierCount: number;
}

// 공급업체
export interface Supplier {
  id: string;
  code: string;
  name: string;
  supplierGroup: string;
  contactPerson?: string;
  email?: string;
  phone?: string;
  address?: string;
  taxId?: string; // 사업자등록번호
  bankAccount?: string;
  bankName?: string;
  paymentTerms?: string; // 결제조건 (예: "익월말 결제")
  currency: string;
  isActive: boolean;
  totalPurchase: number; // 총 구매금액
  outstandingAmount: number; // 미지급금
  createdAt: string;
  updatedAt: string;
}

// 발주서 품목
export interface PurchaseOrderItem {
  id: string;
  itemCode: string;
  itemName: string;
  quantity: number;
  receivedQty: number;
  uom: string;
  unitPrice: number;
  amount: number;
  warehouse?: string;
}

// 발주서
export interface PurchaseOrder {
  id: string;
  code: string;
  supplierId: string;
  supplierName: string;
  status: PurchaseOrderStatus;
  orderDate: string;
  requiredDate: string;
  items: PurchaseOrderItem[];
  totalQty: number;
  totalAmount: number;
  taxAmount: number;
  grandTotal: number;
  receivedQty: number;
  billedAmount: number;
  remarks?: string;
  createdAt: string;
  updatedAt: string;
}

// 입고 품목
export interface PurchaseReceiptItem {
  id: string;
  itemCode: string;
  itemName: string;
  quantity: number;
  uom: string;
  unitPrice: number;
  amount: number;
  warehouse: string;
  purchaseOrderId?: string;
}

// 입고
export interface PurchaseReceipt {
  id: string;
  code: string;
  supplierId: string;
  supplierName: string;
  purchaseOrderId?: string;
  purchaseOrderCode?: string;
  status: PurchaseReceiptStatus;
  receiptDate: string;
  items: PurchaseReceiptItem[];
  totalQty: number;
  totalAmount: number;
  warehouse: string;
  remarks?: string;
  createdAt: string;
  updatedAt: string;
}

// 구매 청구서 품목
export interface PurchaseInvoiceItem {
  id: string;
  itemCode: string;
  itemName: string;
  quantity: number;
  uom: string;
  unitPrice: number;
  amount: number;
  purchaseOrderId?: string;
  purchaseReceiptId?: string;
}

// 구매 청구서
export interface PurchaseInvoice {
  id: string;
  code: string;
  supplierId: string;
  supplierName: string;
  purchaseOrderId?: string;
  purchaseOrderCode?: string;
  status: PurchaseInvoiceStatus;
  invoiceDate: string;
  dueDate: string;
  items: PurchaseInvoiceItem[];
  totalAmount: number;
  taxAmount: number;
  grandTotal: number;
  paidAmount: number;
  outstandingAmount: number;
  remarks?: string;
  createdAt: string;
  updatedAt: string;
}

// 자재 요청 품목
export interface MaterialRequestItem {
  id: string;
  itemCode: string;
  itemName: string;
  quantity: number;
  orderedQty: number;
  receivedQty: number;
  uom: string;
  warehouse: string;
}

// 자재 요청
export interface MaterialRequest {
  id: string;
  code: string;
  type: MaterialRequestType;
  status: MaterialRequestStatus;
  requestDate: string;
  requiredDate: string;
  requestedBy: string;
  department?: string;
  items: MaterialRequestItem[];
  totalQty: number;
  remarks?: string;
  createdAt: string;
  updatedAt: string;
}

// 상태 레이블 및 색상
export const purchaseOrderStatusConfig: Record<
  PurchaseOrderStatus,
  { label: string; color: string }
> = {
  draft: { label: '임시저장', color: 'gray' },
  submitted: { label: '제출됨', color: 'blue' },
  to_receive: { label: '입고대기', color: 'yellow' },
  to_bill: { label: '청구대기', color: 'orange' },
  completed: { label: '완료', color: 'green' },
  cancelled: { label: '취소', color: 'red' },
};

export const purchaseReceiptStatusConfig: Record<
  PurchaseReceiptStatus,
  { label: string; color: string }
> = {
  draft: { label: '임시저장', color: 'gray' },
  submitted: { label: '제출됨', color: 'blue' },
  completed: { label: '완료', color: 'green' },
  cancelled: { label: '취소', color: 'red' },
};

export const purchaseInvoiceStatusConfig: Record<
  PurchaseInvoiceStatus,
  { label: string; color: string }
> = {
  draft: { label: '임시저장', color: 'gray' },
  submitted: { label: '제출됨', color: 'blue' },
  unpaid: { label: '미지급', color: 'yellow' },
  partial: { label: '부분지급', color: 'orange' },
  paid: { label: '완결', color: 'green' },
  overdue: { label: '연체', color: 'red' },
  cancelled: { label: '취소', color: 'gray' },
};

export const materialRequestStatusConfig: Record<
  MaterialRequestStatus,
  { label: string; color: string }
> = {
  draft: { label: '임시저장', color: 'gray' },
  submitted: { label: '제출됨', color: 'blue' },
  pending: { label: '검토중', color: 'yellow' },
  partially_ordered: { label: '부분발주', color: 'orange' },
  ordered: { label: '발주완료', color: 'cyan' },
  received: { label: '입고완료', color: 'green' },
  cancelled: { label: '취소', color: 'red' },
};

export const materialRequestTypeConfig: Record<
  MaterialRequestType,
  { label: string; color: string }
> = {
  purchase: { label: '구매', color: 'blue' },
  material_transfer: { label: '자재이동', color: 'purple' },
  material_issue: { label: '자재출고', color: 'orange' },
  manufacture: { label: '제조', color: 'green' },
};
